import Backbone from 'backbone';

import { StringHelper } from '@biteinc/helpers';

import { convertCsvLinesToCsvData } from '../csv_builder';
import { KioskImportHeaders, parseKioskImportCsv } from '../kiosk_import_csv_parser';

app.KioskImportModal = Backbone.View.extend({
  className: 'kiosk-import-modal',

  setSuperView(superView) {
    this.superView = superView;
  },

  clearState() {
    this.superView = null;
    this._selectedFile = null;
  },

  _downloadCsvTemplate() {
    const aLink = document.createElement('a');
    aLink.download = 'kiosk-import-template.csv';
    aLink.href = convertCsvLinesToCsvData([KioskImportHeaders]);
    aLink.dispatchEvent(new MouseEvent('click'));
  },

  _runImport() {
    const reader = new FileReader();
    reader.onload = () => {
      const { errorMessages, kioskRows } = parseKioskImportCsv(reader.result);
      if (errorMessages.length) {
        const errorListHtml = errorMessages
          .map((errorMessage) => `<li>${StringHelper.escapeHtml(errorMessage)}</li>`)
          .join('');
        new app.AlertView().show('Cannot import this CSV file', `<ol>${errorListHtml}</ol>`);
        return;
      }

      const locationCount = new Set(kioskRows.map((kioskRow) => kioskRow.locationId)).size;
      const locationsDescription = `${locationCount} location${locationCount === 1 ? '' : 's'}`;
      const confirmed = window.confirm(
        `Import ${kioskRows.length} kiosks into ${locationsDescription} of ${app.org.get('name')}?`,
      );
      if (!confirmed) {
        return;
      }

      const $importButton = this._$importButton;
      const reqId = $importButton.initLoadingButton(
        $importButton.html(),
        'Importing...',
        'Imported!',
      );
      app.makeRequestWithOptions({
        method: 'POST',
        url: '/api/v2/kiosks/import',
        data: { kiosks: kioskRows },
        onSuccess: (data) => {
          $importButton.loadingDidFinishSuccessfully(reqId);
          this.trigger(app.ModalView.Events.ModalViewShouldHide, this);
          const summaryListHtml = (data.summaryDescriptions || [])
            .map((description) => `<li>${StringHelper.escapeHtml(description)}</li>`)
            .join('');
          new app.AlertView().show(
            `Imported ${kioskRows.length} kiosks into ${locationsDescription}`,
            `<ul>${summaryListHtml}</ul>`,
          );
        },
        onFailure: () => {
          $importButton.loadingDidFinishWithError(reqId);
        },
      });
    };
    reader.readAsText(this._selectedFile);
  },

  render() {
    this.superView.$titleLabel.text('Import Kiosks from CSV');
    this.superView.footer.$el.hide();

    const $step1 = $(
      // prettier-ignore
      '<div class="mb-4">' +
        '<div class="mb-2">' +
          '<strong>Step 1.</strong> Download the blank CSV template and fill in one row per kiosk.' +
        '</div>' +
      '</div>',
    );
    const $downloadButton = $(
      '<button type="button" class="btn btn-sm btn-primary">Download Blank CSV</button>',
    );
    $downloadButton.click(() => {
      this._downloadCsvTemplate();
    });
    $step1.append($downloadButton);

    const $step2 = $(
      // prettier-ignore
      '<div class="mb-4">' +
        '<div class="mb-2">' +
          '<strong>Step 2.</strong> Upload the filled-in file.' +
          '<ul class="mt-1 mb-2 text-muted small">' +
            '<li><strong>Serial Number</strong>: The device serial number; must be new to Bite.</li>' +
            '<li><strong>Location ID</strong>: The Bite ID of a kiosk channel in this org.</li>' +
            '<li><strong>Name</strong>: Optional; blank names the kiosk after its prefix, e.g. "Kiosk A".</li>' +
            '<li><strong>Kiosk Type / Printer / Payment Terminal</strong>: The names shown in the kiosk form, e.g. "Android MicroTouch 22&quot;", "TM-T88V Thermal", "No Payment Terminal".</li>' +
            '<li><strong>Printer MAC Address</strong>: Required for a non-built-in printer; "USB Printer" for a USB printer.</li>' +
            '<li><strong>Payment Terminal Identifier / IP Address</strong>: Optional; as in the kiosk form.</li>' +
            '<li><strong>Scanner / HID / MSR / Scale / Storm Audio Nav</strong>: TRUE, FALSE, or blank.</li>' +
            '<li><strong>Notes</strong>: Optional.</li>' +
          '</ul>' +
          'Prefixes are assigned in file order: A–Z, 1–9, then AA, AB, …' +
        '</div>' +
      '</div>',
    );
    const $fileInput = $('<input type="file" accept=".csv,text/csv" class="d-none">');
    const $chooseFileButton = $(
      '<button type="button" class="btn btn-sm btn-primary">Choose File</button>',
    );
    const $fileNameLabel = $('<span class="ms-2 text-muted">No file selected</span>');
    $chooseFileButton.click(() => {
      $fileInput.trigger('click');
    });
    $fileInput.change(() => {
      const file = $fileInput[0].files[0];
      // Clear the input so that re-selecting the same file fires this handler again.
      $fileInput.val('');
      if (!file) {
        return;
      }
      this._selectedFile = file;
      $fileNameLabel.text(file.name);
      this._$importButton.prop('disabled', false);
    });
    $step2.append($fileInput, $chooseFileButton, $fileNameLabel);

    const $step3 = $(
      // prettier-ignore
      '<div class="mb-2">' +
        '<div class="mb-2">' +
          '<strong>Step 3.</strong> Run the import. Nothing is imported unless every row is valid.' +
        '</div>' +
      '</div>',
    );
    this._$importButton = $(
      '<button type="button" class="btn btn-sm btn-primary" disabled>Import Kiosks</button>',
    );
    this._$importButton.click(() => {
      this._runImport();
    });
    $step3.append(this._$importButton);

    this.$el.append($step1, $step2, $step3);
    return this;
  },
});
